import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, client } from '@xinfer/api-client';
import { Overview } from './pages/Overview';
import { Keys } from './pages/Keys';
import { Usage } from './pages/Usage';
import { Limits } from './pages/Limits';
import { Quickstart } from './pages/Quickstart';

const PAGES = ['Overview', 'Keys', 'Usage', 'Limits', 'Quickstart'] as const;
type Page = (typeof PAGES)[number];

export function App() {
  const [page, setPage] = useState<Page>('Overview');
  const queryClient = useQueryClient();

  const me = useQuery({
    queryKey: ['me'],
    queryFn: () => client.me(),
    // 401 is not a failure to retry: it means "show the login page".
    retry: (count, error) => !(error instanceof ApiError && error.isUnauthenticated) && count < 2,
  });

  const logout = useMutation({
    mutationFn: () => client.logout(),
    onSuccess: () => queryClient.clear(),
  });

  if (me.isLoading) {
    return <p className="muted" style={{ padding: 32 }}>Loading…</p>;
  }

  if (me.error instanceof ApiError && me.error.isUnauthenticated) {
    return <SignIn onSignedIn={() => queryClient.invalidateQueries({ queryKey: ['me'] })} />;
  }

  if (me.isError || !me.data) {
    return <p className="error" style={{ padding: 32 }}>The control plane is unreachable.</p>;
  }

  // Every account has exactly one member today, so there is no switcher and no
  // members page. `default_account_id` is what the SPA follows; when a second
  // account appears, this is where a switcher goes and nothing else changes.
  const account = me.data.accounts.find((a) => a.id === me.data?.default_account_id);
  if (!account) {
    return <p className="error" style={{ padding: 32 }}>This login has no account.</p>;
  }

  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="brand">xinfer</div>
        <nav className="nav">
          {PAGES.map((name) => (
            <button
              key={name}
              onClick={() => setPage(name)}
              aria-current={page === name ? 'page' : undefined}
            >
              {name}
            </button>
          ))}
        </nav>
        <div style={{ marginTop: 24, padding: '0 10px' }}>
          <div className="muted" style={{ fontSize: 13, wordBreak: 'break-all' }}>
            {me.data.user.name ?? me.data.user.email ?? me.data.user.user_id}
          </div>
          <button
            className="ghost"
            style={{ marginTop: 10, width: '100%' }}
            onClick={() => logout.mutate()}
          >
            Sign out
          </button>
        </div>
      </aside>

      <main className="main">
        {page === 'Overview' && <Overview account={account} />}
        {page === 'Keys' && <Keys account={account} />}
        {page === 'Usage' && <Usage account={account} />}
        {page === 'Limits' && <Limits account={account} />}
        {page === 'Quickstart' && <Quickstart account={account} />}
      </main>
    </div>
  );
}

/**
 * Sign-in, rendered from what the api says it accepts.
 *
 * Every method here is conditional on `GET /v1/auth/providers`, because none
 * of them exist unconditionally: OAuth providers are configured per
 * deployment, and `dev-login` is not routed at all without `--dev-login`. This
 * component previously rendered the dev form unconditionally, which meant the
 * production portal offered exactly one button and it posted to a 404 — while
 * the method that did work had no button at all.
 *
 * The OAuth buttons are links, not fetches: the api answers with a 307 to the
 * provider, and that has to happen in the address bar.
 */
const PROVIDER_LABELS: Record<string, string> = {
  github: 'GitHub',
  google: 'Google',
};

function SignIn({ onSignedIn }: { onSignedIn: () => void }) {
  const [email, setEmail] = useState('');
  const methods = useQuery({
    queryKey: ['auth-methods'],
    queryFn: () => client.authMethods(),
  });
  const signIn = useMutation({
    mutationFn: (value: string) => client.devLogin(value),
    onSuccess: onSignedIn,
  });

  if (methods.isLoading) {
    return (
      <div className="login">
        <h1>Sign in to xinfer</h1>
        <p className="muted">Loading…</p>
      </div>
    );
  }

  // Failing closed is deliberate: guessing a form here is what produced a
  // button that could not work.
  if (methods.isError || !methods.data) {
    return (
      <div className="login">
        <h1>Sign in to xinfer</h1>
        <p className="error">The control plane is unreachable, so sign-in is unavailable.</p>
      </div>
    );
  }

  const { providers, dev_login: devLogin } = methods.data;

  // The api logs this at startup too, but nobody reading the portal sees the
  // log. Saying so beats an empty page that looks like it is still loading.
  if (providers.length === 0 && !devLogin) {
    return (
      <div className="login">
        <h1>Sign in to xinfer</h1>
        <p className="error">
          No sign-in method is configured, so nobody can sign in. Set{' '}
          <code>XINFER_GITHUB_OAUTH</code> or <code>XINFER_GOOGLE_OAUTH</code> on the api.
        </p>
      </div>
    );
  }

  return (
    <div className="login">
      <h1>Sign in to xinfer</h1>

      {providers.length > 0 && (
        <>
          <p className="subtitle">Continue with your account.</p>
          {providers.map((provider) => (
            <a
              key={provider}
              className="primary"
              href={client.loginUrl(provider)}
              style={{
                display: 'block',
                marginTop: 10,
                width: '100%',
                textAlign: 'center',
                boxSizing: 'border-box',
                textDecoration: 'none',
              }}
            >
              Continue with {PROVIDER_LABELS[provider] ?? provider}
            </a>
          ))}
        </>
      )}

      {devLogin && (
        <>
          <p className="subtitle" style={{ marginTop: providers.length > 0 ? 26 : 0 }}>
            Development sign-in. This api was started with <code>--dev-login</code>.
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (email) signIn.mutate(email);
            }}
          >
            <input
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              aria-label="Email"
            />
            <button
              className="primary"
              type="submit"
              style={{ marginTop: 12, width: '100%' }}
              disabled={!email || signIn.isPending}
            >
              {signIn.isPending ? 'Signing in…' : 'Continue'}
            </button>
          </form>
          {signIn.isError && <p className="error">Sign-in failed.</p>}
        </>
      )}
    </div>
  );
}
